import { useCallback } from 'react'
import {
  hasAllPermissions,
  hasAnyPermissions,
} from '../../helpers/hasPermission'
import { RbacPermissions, PermissionCheckMode } from '../../types/access'
import { useUserPermissions } from '../useUserPermissions'

type UseHavePermissionsProps = {
  scope?: string
}

type UseHavePermissionsReturn = {
  isLoading: boolean
  isError: boolean
  checkPermissions: (
    requiredPermissions: RbacPermissions,
    mode?: PermissionCheckMode,
  ) => boolean
}

export const useHavePermissions = ({
  scope,
}: UseHavePermissionsProps = {}): UseHavePermissionsReturn => {
  const { userPermissions, isLoading, isError } = useUserPermissions(scope)

  const checkPermissions = useCallback(
    (
      requiredPermissions: RbacPermissions,
      mode: PermissionCheckMode = 'all',
    ): boolean => {
      if (!requiredPermissions?.resourceAccess?.length) {
        return true
      }

      if (isError || isLoading || !userPermissions) {
        return false
      }

      const permissions = requiredPermissions.resourceAccess
      return mode === 'any'
        ? hasAnyPermissions(permissions, userPermissions)
        : hasAllPermissions(permissions, userPermissions)
    },
    [userPermissions, isLoading, isError, scope],
  )

  return {
    isLoading,
    isError,
    checkPermissions,
  }
}
